import React, { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const LectionaryCalendar = ({
  selectedDate,
  onDateSelect,
  currentMonth,
  currentYear,
  onMonthChange,
}) => {
  const [showMonthPicker, setShowMonthPicker] = useState(false);

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
  const firstDayOfMonth = new Date(currentYear, currentMonth, 1).getDay();

  const handlePrevMonth = () => {
    if (currentMonth === 0) {
      onMonthChange(11);
    } else {
      onMonthChange(currentMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (currentMonth === 11) {
      onMonthChange(0);
    } else {
      onMonthChange(currentMonth + 1);
    }
  };

  const handleMonthSelect = (monthIndex) => {
    onMonthChange(monthIndex);
    setShowMonthPicker(false);
  };

  const handleDateClick = (day) => {
    // Format date as YYYY-MM-DD without timezone conversion
    const year = currentYear;
    const month = String(currentMonth + 1).padStart(2, "0");
    const dayStr = String(day).padStart(2, "0");
    const dateString = `${year}-${month}-${dayStr}`;
    onDateSelect(dateString);
  };

  const isSelectedDate = (day) => {
    if (!selectedDate) return false;
    // Format date as YYYY-MM-DD without timezone conversion
    const year = currentYear;
    const month = String(currentMonth + 1).padStart(2, "0");
    const dayStr = String(day).padStart(2, "0");
    const dateString = `${year}-${month}-${dayStr}`;
    return dateString === selectedDate;
  };

  const isToday = (day) => {
    const today = new Date();
    return (
      day === today.getDate() &&
      currentMonth === today.getMonth() &&
      currentYear === today.getFullYear()
    );
  };

  // Generate array of days for the calendar grid
  const calendarDays = [];
  // Add empty cells for days before the first day of the month
  for (let i = 0; i < firstDayOfMonth; i++) {
    calendarDays.push(null);
  }
  // Add all days of the month
  for (let day = 1; day <= daysInMonth; day++) {
    calendarDays.push(day);
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      {/* Calendar Header */}
      <div className="flex items-center justify-between mb-6">
        {showMonthPicker ? (
          <div className="w-10"></div>
        ) : (
          <button
            onClick={handlePrevMonth}
            disabled={currentMonth === 0}
            className={`p-2 ${
              currentMonth === 0 ? "" : "hover:bg-gray-100"
            } rounded-full transition-colors disabled:opacity-0`}
            aria-label="Previous month"
          >
            <ChevronLeft className="w-5 h-5 text-gray-600" />
          </button>
        )}

        {showMonthPicker ? (
          <div className="text-xl font-semibold text-gray-900">
            {currentYear}
          </div>
        ) : (
          <button
            onClick={() => setShowMonthPicker(true)}
            className="text-xl font-semibold text-gray-900 hover:text-red-600 transition-colors"
          >
            {monthNames[currentMonth]} {currentYear}
          </button>
        )}

        {showMonthPicker ? (
          <div className="w-10"></div>
        ) : (
          <button
            onClick={handleNextMonth}
            disabled={currentMonth === 11}
            className={`p-2 ${
              currentMonth === 11 ? "" : "hover:bg-gray-100"
            } rounded-full transition-colors disabled:opacity-0`}
            aria-label="Next month"
          >
            <ChevronRight className="w-5 h-5 text-gray-600" />
          </button>
        )}
      </div>

      {/* Month Picker */}
      {showMonthPicker && (
        <div className="mb-4 p-4 bg-gray-50 rounded-lg">
          <div className="grid grid-cols-3 gap-2">
            {monthNames.map((month, index) => (
              <button
                key={month}
                onClick={() => handleMonthSelect(index)}
                className={`p-3 rounded transition-colors ${
                  index === currentMonth
                    ? "bg-red-600 text-white"
                    : "bg-white hover:bg-gray-200 text-gray-700"
                }`}
              >
                {month}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Calendar Grid */}
      {!showMonthPicker && (
        <div className="grid grid-cols-7 gap-1">
          {/* Day headers */}
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
            <div
              key={day}
              className="text-center font-semibold text-gray-600 py-2 text-sm"
            >
              {day}
            </div>
          ))}

          {/* Calendar days */}
          {calendarDays.map((day, index) => (
            <div key={index} className="aspect-square">
              {day ? (
                <button
                  onClick={() => handleDateClick(day)}
                  className={`w-full h-full flex items-center justify-center rounded transition-all ${
                    isSelectedDate(day)
                      ? "bg-red-600 text-white font-bold"
                      : isToday(day)
                      ? "bg-red-100 text-red-600 font-semibold"
                      : "hover:bg-gray-100 text-gray-700"
                  }`}
                >
                  {day}
                </button>
              ) : (
                <div className="w-full h-full" />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default LectionaryCalendar;
